import { type DriveStep, driver } from 'driver.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'driver.js/dist/driver.css';
import '../styles/driver-tour.css';
import { useAuth } from '../contexts/auth-context';
import { sharedDriverConfig } from '../tours/tour-config';
import { safeGetItem, safeRemoveItem, safeSetItem } from '../utils/safe-storage';

// Module-level singleton guard — only one tour can run at a time.
// driver.js uses a single overlay, so concurrent tours corrupt each other.
let globalActiveTourId: string | null = null;

interface UseProductTourOptions {
  tourId: string;
  steps: DriveStep[];
  autoStart?: boolean;
  autoStartDelay?: number;
  disabled?: boolean;
}

interface UseProductTourReturn {
  startTour: () => void;
  isCompleted: boolean;
  resetTour: () => void;
}

function getTourKey(tourId: string, userId: string): string {
  return `expertmd_tour_${tourId}_${userId}`;
}

export function useProductTour({
  tourId,
  steps,
  // Tours are opt-in by default: they start only via an explicit trigger
  // (Help menu event or startTour). Pass autoStart to launch on mount.
  autoStart = false,
  autoStartDelay = 800,
  disabled = false,
}: UseProductTourOptions): UseProductTourReturn {
  const { user } = useAuth();
  const userId = user?.id || user?.userId || '';
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const mountedRef = useRef(true);
  const autoStartedRef = useRef(false);
  // [FIX #6] Track whether a tour is actively running to prevent concurrent conflicts
  const isRunningRef = useRef(false);
  // Ref for disabled so startTour (via manual event) can check current value without stale closure
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;
  // Track whether a running tour was interrupted by disabled=true, to prevent
  // re-auto-start when disabled transitions back to false.
  const wasInterruptedRef = useRef(false);

  // [FIX #5] Stabilize steps reference to prevent re-render loops when callers
  // pass inline array literals. JSON.stringify is safe here because DriveStep
  // values are serializable config objects.
  let stepsJson: string;
  try {
    stepsJson = JSON.stringify(steps);
  } catch {
    stepsJson = String(steps.length); // Fallback for non-serializable steps
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally depend on stepsJson (a stable JSON serialization) rather than steps, whose identity changes per render
  const stableSteps = useMemo<DriveStep[]>(() => steps, [stepsJson]);

  // Check completion on mount
  useEffect(() => {
    if (!userId) {
      // No userId yet — mark completed in-memory to prevent auto-start.
      // Once userId becomes available this effect re-runs and reads localStorage.
      setIsCompleted(true);
      return;
    }
    // [FIX #4] Safe localStorage read
    const stored = safeGetItem(getTourKey(tourId, userId));
    setIsCompleted(!!stored);
  }, [tourId, userId]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Clear isRunningRef BEFORE destroy() so that onDestroyStarted (which
      // guards on isRunningRef) does NOT mark the tour as completed during
      // unmount teardown.  This matches the pattern used in the disabled-
      // transition effect below.
      isRunningRef.current = false;
      globalActiveTourId = null;
      if (driverRef.current) {
        // Capture ref locally: destroy() fires onDestroyed synchronously,
        // which nulls driverRef.current — the local var keeps the reference
        // alive for the duration of the call.
        const d = driverRef.current;
        driverRef.current = null;
        d.destroy();
      }
    };
  }, []);

  const markComplete = useCallback(() => {
    // [FIX #1] Guard against setState after unmount
    if (!mountedRef.current) return;
    if (userId) {
      // [FIX #4] Safe localStorage write.
      // Note: if localStorage is unavailable safeSetItem returns false, but
      // setIsCompleted(true) still prevents re-trigger for this session.
      // On next page load the tour will re-appear — this is a known limitation
      // when localStorage is disabled/full.
      safeSetItem(getTourKey(tourId, userId), new Date().toISOString());
      setIsCompleted(true);
    }
  }, [tourId, userId]);

  const startTour = useCallback(() => {
    if (!stableSteps.length || !mountedRef.current || disabledRef.current) return;

    // Global singleton guard — another tour (from a different hook instance) is active
    if (globalActiveTourId && globalActiveTourId !== tourId) {
      return;
    }

    // [FIX #6] Prevent starting a new tour while one is already running.
    // The custom event from TourHelpButton could fire while a tour is active;
    // destroying the active tour would trigger onDestroyStarted and incorrectly
    // mark it as completed.
    if (isRunningRef.current && driverRef.current) {
      return;
    }

    // Filter out steps whose target elements don't exist in DOM or are hidden
    // (e.g. `hidden md:block` desktop-only tables on mobile viewports)
    const availableSteps = stableSteps.filter((step) => {
      if (!step.element) return true; // No element = intro step
      const selector = typeof step.element === 'string' ? step.element : null;
      if (!selector) return true;
      const el = document.querySelector(selector);
      if (!el) return false;
      // Check computed visibility — excludes display:none, visibility:hidden, etc.
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    });

    if (!availableSteps.length) return;

    // [FIX #2] Destroy previous instance if any (defensive — should not happen
    // with isRunningRef guard, but covers edge cases)
    if (driverRef.current) {
      // Destroy without triggering completion
      isRunningRef.current = false;
      globalActiveTourId = null;
      driverRef.current.destroy();
      driverRef.current = null;
    }

    // Guard against re-entrant destroy() calls from onDestroyStarted
    let isDestroying = false;

    const driverObj = driver({
      ...sharedDriverConfig,
      steps: availableSteps,
      onDestroyStarted: () => {
        if (isDestroying) return;
        isDestroying = true;
        // [FIX #6] Only mark complete if we're still the active tour
        if (isRunningRef.current) {
          markComplete();
        }
        isRunningRef.current = false;
        globalActiveTourId = null;
        driverObj.destroy();
      },
      // [FIX #1] Also handle the case where driver is destroyed externally
      // (e.g., by clicking the overlay) — ensure we clean up our ref
      onDestroyed: () => {
        isRunningRef.current = false;
        globalActiveTourId = null;
        if (driverRef.current === driverObj) {
          driverRef.current = null;
        }
      },
    });

    driverRef.current = driverObj;
    isRunningRef.current = true;
    globalActiveTourId = tourId;
    driverObj.drive();
  }, [stableSteps, markComplete, tourId]);

  // [FIX #8] Tear down a running tour when disabled transitions to true.
  // Without this, a tour that started while disabled=false would keep its
  // overlay visible even after a modal (onboarding, unlock, etc.) opens and
  // sets disabled=true. The tour is destroyed WITHOUT marking it as completed
  // so it can re-auto-start next time disabled becomes false.
  useEffect(() => {
    if (disabled && isRunningRef.current && driverRef.current) {
      // Mark as interrupted so auto-start doesn't re-trigger when disabled flips back
      wasInterruptedRef.current = true;
      // Clear the running flag BEFORE destroy() so that the onDestroyStarted
      // callback (which guards on isRunningRef) does NOT mark the tour complete.
      isRunningRef.current = false;
      globalActiveTourId = null;
      // Capture ref locally: destroy() fires onDestroyed synchronously,
      // which nulls driverRef.current via the identity check.  By nulling
      // the ref first and using a local var, we avoid relying on that
      // synchronous side-effect ordering and make double-destroy impossible
      // even if React batches multiple disabled flips.
      const d = driverRef.current;
      driverRef.current = null;
      d.destroy();
    }
  }, [disabled]);

  // Auto-start logic with retry for lazy-loaded elements
  useEffect(() => {
    // When disabled flips to false, reset the autoStarted flag so the tour
    // can be re-attempted — but only if the tour wasn't interrupted by a
    // disabled=true transition. Without this guard, dismissing a modal that
    // set disabled=true would cause the interrupted tour to re-auto-start.
    if (!disabled && !wasInterruptedRef.current) {
      autoStartedRef.current = false;
    }

    if (
      !autoStart ||
      disabled ||
      isCompleted ||
      !userId ||
      !stableSteps.length ||
      autoStartedRef.current
    ) {
      return;
    }

    // [FIX #2] Use a cancellation flag scoped to this effect invocation
    // so that stale retry chains from a previous effect run don't fire.
    let cancelled = false;
    let retryCount = 0;
    const maxRetries = 5;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tryStart = () => {
      // [FIX #2] Check both the ref and the local cancellation flag
      if (!mountedRef.current || autoStartedRef.current || cancelled) return;

      // Check if first step's element exists and is visible
      const firstStep = stableSteps[0];
      const selector = typeof firstStep?.element === 'string' ? firstStep.element : null;

      if (selector) {
        const el = document.querySelector(selector);
        if (!el || window.getComputedStyle(el).display === 'none') {
          if (retryCount < maxRetries) {
            retryCount++;
            timeoutId = setTimeout(tryStart, 500);
            return;
          }
          // Max retries reached -- skip auto-start
          return;
        }
      }

      autoStartedRef.current = true;
      startTour();
    };

    timeoutId = setTimeout(tryStart, autoStartDelay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [autoStart, disabled, isCompleted, userId, stableSteps, autoStartDelay, startTour]);

  // Listen for manual tour start from TourHelpButton
  useEffect(() => {
    const handleManualStart = (e: Event) => {
      // [FIX #3 & #7] Properly narrow the CustomEvent type instead of raw `as` cast
      if (!(e instanceof CustomEvent)) return;
      const detail: unknown = e.detail;
      if (
        typeof detail === 'object' &&
        detail !== null &&
        'tourId' in detail &&
        (detail as { tourId: unknown }).tourId === tourId
      ) {
        startTour();
      }
    };
    window.addEventListener('start-product-tour', handleManualStart);
    return () => window.removeEventListener('start-product-tour', handleManualStart);
  }, [tourId, startTour]);

  // Listen for tours-reset event (fired by TourHelpButton "Reset All Tours")
  useEffect(() => {
    const handleReset = () => {
      if (mountedRef.current) {
        setIsCompleted(false);
        // Don't reset autoStartedRef here — resetting tours should not
        // cause an unexpected auto-start in the current session. The user
        // can manually trigger tours from the help menu after reset.
      }
    };
    window.addEventListener('tours-reset', handleReset);
    return () => window.removeEventListener('tours-reset', handleReset);
  }, []);

  const resetTour = useCallback(() => {
    if (userId) {
      // [FIX #4] Safe localStorage removal
      safeRemoveItem(getTourKey(tourId, userId));
      if (mountedRef.current) {
        setIsCompleted(false);
      }
      autoStartedRef.current = false;
    }
  }, [tourId, userId]);

  return { startTour, isCompleted, resetTour };
}
